<template>
  <div data-qa="push-test-send-dialog">
    <el-dialog
      width="600px"
      :visible="true"
      :close-on-click-modal="false"
      :modal-append-to-body="false"
      :modal="false"
      :before-close="close"
    >
      <template #title>
        <div class="el-dialog__title" data-qa="push-test-send-dialog-title">
          {{ title }}
        </div>
      </template>
      <div
        v-if="profileLabel"
        class="profile-description"
        data-qa="push-test-send-dialog-profile-description"
      >
        {{
          $t('CampaignForm_Preview_Send_Description', { profile: profileLabel })
        }}
      </div>
      <div
        v-else
        class="profile-description profile-description--muted"
        data-qa="push-test-send-dialog-profile-description-empty"
      >
        {{ $t('CampaignForm_Preview_Send_Description_NoProfile') }}
      </div>
      <label class="inline">
        {{ $t('CampaignForm_Preview_Send_Push_To') }}
      </label>
      <form-item class="inline" prop="recipients">
        <el-select
          v-model="recipients"
          :multiple-limit="recipientsLimit"
          :placeholder="
            $t('CampaignForm_Preview_Send_Push_Recipient_Placeholder')
          "
          multiple
          filterable
          allow-create
          default-first-option
          data-qa="push-test-send-dialog-recipients"
        >
          <el-option
            v-for="item in savedOptions"
            :key="item"
            :label="item"
            :value="item"
          />
        </el-select>
        <div
          :class="['recipients-hint', { 'is-at-limit': isAtRecipientsLimit }]"
          data-qa="push-test-send-dialog-recipients-hint"
        >
          <i
            v-if="isAtRecipientsLimit"
            class="el-icon-warning-outline recipients-hint-icon"
          />
          {{ recipientsHint }}
        </div>
      </form-item>
      <div v-loading="loading" data-qa="push-test-send-dialog-body">
        <div
          v-if="results.length"
          class="results-ct"
          data-qa="push-test-send-dialog-results"
        >
          <div
            v-for="(recipient, idx) in results"
            :key="idx"
            class="result-row"
            :data-qa="`push-test-send-dialog-result-${idx}`"
          >
            <div
              class="result-identifier"
              data-qa="push-test-send-dialog-result-identifier"
            >
              <span
                v-if="identifierTypeLabel(recipient)"
                class="result-identifier-type"
                data-qa="push-test-send-dialog-result-identifier-type"
              >
                {{ identifierTypeLabel(recipient) }}:
              </span>
              <span
                class="result-identifier-value"
                data-qa="push-test-send-dialog-result-identifier-value"
              >
                {{ recipient.identifier }}
              </span>
            </div>
            <div
              v-if="recipient.error"
              class="result-error"
              data-qa="push-test-send-dialog-result-error"
            >
              {{ recipient.error_message }}
            </div>
            <div
              v-else-if="recipient.deduplicated"
              class="result-deduplicated"
              data-qa="push-test-send-dialog-result-deduplicated"
            >
              {{ $t('CampaignForm_Preview_Send_Push_AlreadySent') }}
            </div>
            <ul v-else class="result-devices">
              <li
                v-for="device in recipient.devices"
                :key="device.device_id"
                :class="['result-device', deviceStatusClass(device)]"
                :data-qa="
                  `push-test-send-dialog-result-device-${device.device_id}`
                "
              >
                <span class="result-status">{{
                  deviceStatusLabel(device)
                }}</span>
                <span v-if="device.error_message" class="result-error">
                  {{ $t(device.error_message) }}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="send-button">
        <el-button
          :disabled="disableInput"
          icon="el-icon-s-promotion"
          type="primary"
          data-qa="push-test-send-dialog-send-button"
          @click="sendPreview"
        >
          {{ $t('CampaignForm_Preview_Send_Push_Send_Button') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import FormItem from '@/common/components/form/FormItem.vue'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import { Message } from 'element-ui'
import { getProfileLabel } from '@/modules/campaign-manage/helpers/ProfileLabelHelper'
import { channelTypes } from '@/modules/campaign-manage/enum/Labels'

const RECIPIENTS_LIMIT = 10

export default {
  name: 'PushTestSendDialog',
  components: { FormItem },
  props: {
    dialogVisible: {
      type: Boolean,
      required: true
    },
    preview: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      recipients: [],
      savedOptions: [],
      results: [],
      loading: false
    }
  },
  computed: {
    disableInput() {
      return this.loading || !this.recipients.length
    },
    profileLabel() {
      return getProfileLabel(this.preview && this.preview.selected_profile)
    },
    title() {
      // Title flips between "Send yourself a test iOS Push" and the Android
      // equivalent based on which platform the campaign tab the operator opened
      // is targeting. Falls back to the generic copy when message_type isn't a
      // recognised push channel — the dialog isn't expected to mount in that
      // case, but the fallback keeps it self-explanatory if it ever does.
      const messageType = this.preview && this.preview.message_type
      if (messageType === channelTypes.ios_push) {
        return this.$t('CampaignForm_Preview_Send_Push_Title_Ios')
      }
      if (messageType === channelTypes.android_push) {
        return this.$t('CampaignForm_Preview_Send_Push_Title_Android')
      }
      return this.$t('CampaignForm_Preview_Send_Push_Title')
    },
    recipientsLimit() {
      return RECIPIENTS_LIMIT
    },
    isAtRecipientsLimit() {
      return this.recipients.length >= RECIPIENTS_LIMIT
    },
    recipientsHint() {
      const count = this.recipients.length
      const limit = RECIPIENTS_LIMIT
      if (this.isAtRecipientsLimit) {
        return this.$t('CampaignForm_Preview_Send_Recipients_LimitReached', {
          limit
        })
      }
      return this.$t('CampaignForm_Preview_Send_Recipients_Count', {
        count,
        limit
      })
    }
  },
  watch: {
    recipients(value) {
      this.savedOptions = [...new Set([...(value || []), ...this.savedOptions])]
    }
  },
  methods: {
    close() {
      this.$emit('update:dialogVisible', false)
    },
    deviceStatusClass(device) {
      if (device.error) return 'is-error'
      return 'is-sent'
    },
    deviceStatusLabel(device) {
      // The configured app title disambiguates two same-platform apps registered
      // against one profile, which the platform string alone can't. Fall back to
      // the platform name when the backend didn't supply an app title (older
      // payloads, or an app that's been deleted but still has live device tokens).
      // Failure rows label the same way as success rows — the per-device error
      // message rendered alongside (`device.error_message`) carries the actionable
      // detail (cert expired, push disabled, BadDeviceToken, …); a generic "couldn't
      // be reached" line on top would only repeat that the row already shows.
      const target =
        device.application_name || this.formatPlatform(device.platform)
      if (device.error) {
        return this.$t('CampaignForm_Preview_Send_Push_FailedFor', { target })
      }
      return this.$t('CampaignForm_Preview_Send_Push_SentTo', { target })
    },
    formatPlatform(platform) {
      // Backend stores platform types as `ios`/`android` to match the SDK contract;
      // the dashboard surfaces them as proper-cased product names. Falls back to
      // the raw value so an unexpected platform isn't swallowed silently.
      switch (platform) {
        case 'ios':
          return 'iOS'
        case 'android':
          return 'Android'
        default:
          return platform
      }
    },
    identifierTypeLabel(recipient) {
      // Label echoes which Profile field the operator's input matched so they can
      // confirm at a glance which axis of the profile they actually targeted —
      // useful when one profile is reachable via several identifiers.
      switch (recipient && recipient.identifier_type) {
        case 'profile_id':
          return this.$t('CampaignForm_Preview_Send_IdentifierType_ProfileId')
        case 'email':
          return this.$t('CampaignForm_Preview_Send_IdentifierType_Email')
        case 'user_id':
          return this.$t('CampaignForm_Preview_Send_IdentifierType_UserId')
        default:
          return null
      }
    },
    sendPreview() {
      this.loading = true
      this.results = []
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `campaign-test-sends`,
          data: {
            campaign: this.preview.campaign,
            message_type: this.preview.message_type,
            variant_key: this.preview.variant_key,
            workflow_action: this.preview.workflow_action,
            language: this.preview.language,
            profile_id: this.preview.profile_id,
            customAttributes: this.preview.customAttributes,
            recipients: this.recipients
          }
        })
      )
        .then(({ data }) => {
          this.results = data.recipients || []
          if (data.error) {
            this.$message({
              message:
                data.error_message ||
                this.$t('CampaignForm_Preview_Send_Push_PartialFailure'),
              type: 'error',
              duration: 5000
            })
          }
        })
        .catch(error => {
          Message.closeAll()
          this.$message({
            message: error.message,
            type: 'error',
            duration: 5000
          })
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped lang="scss">
.profile-description {
  margin-bottom: 12px;
  font-size: 13px;
  color: #606266;
}
.profile-description--muted {
  color: #909399;
  font-style: italic;
}

.inline {
  display: inline-block;
}
.el-form-item {
  margin-right: 5px;
  width: 90%;
}

.recipients-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}

.recipients-hint.is-at-limit {
  color: #e6a23c;
}

.recipients-hint-icon {
  margin-right: 4px;
}

.results-ct {
  margin-top: 16px;
  border-top: 1px solid #e4e7ed;
  padding-top: 12px;
}

.result-row {
  margin-bottom: 12px;
}

.result-row + .result-row {
  border-top: 1px solid #e4e7ed;
  padding-top: 12px;
}

.result-identifier {
  font-weight: 600;
  margin-bottom: 4px;
}

.result-identifier-type {
  color: #909399;
  font-weight: 500;
  margin-right: 4px;
}

.result-devices {
  list-style: none;
  margin: 0;
  padding-left: 12px;
}

.result-device {
  padding: 4px 0;
  display: flex;
  gap: 12px;
  align-items: baseline;
}

.result-device.is-error {
  color: #f56c6c;
}
.result-device.is-sent {
  color: #67c23a;
}

.result-error {
  color: #f56c6c;
  margin-top: 4px;
}

.result-deduplicated {
  color: #909399;
  margin-top: 4px;
  font-style: italic;
}

.send-button {
  display: flex;
  justify-content: right;
  margin-top: 12px;
}

/deep/ .el-dialog__header {
  background-color: #1d87c8;
}

/deep/ .el-dialog__headerbtn {
  border-color: #ffffff;
}

/deep/ .el-dialog__title {
  color: #ffffff;
}

/deep/ .el-dialog__headerbtn i {
  color: #ffffff;
  -webkit-text-stroke-color: #ffffff;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/preview/PushTestSendDialog.vue